import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { WebMap } from '../constructors';
import { logError, logWarn } from '../../monitoring/log';

/**
 *
 * sentry will search by title string for matching, change this string need to update in sentry alert as well.
 * all esri map related sentry error will prefix `esri map:`.
 */
export const getEsriMapErrorTitle = (text: string) => {
    return `esri map: ${text}`;
};

export const logEsriError = ({
    error,
    category,
    errorMsg,
    isEmbeddedMap,
    extraData,
}: {
    error?: Error;
    category:
        | 'interceptor_invalid_token' // invalid token detected by request interceptor
        | 'interceptor_basemap_load_error' // basemap load error detected by request interceptor
        | 'interceptor_portals_error' // portals request error detected by request interceptor
        | 'invalid_token' // invalid token detected by esri map load
        | 'basemap_load_error' // basemap load error detected by esri map load
        | 'webgl_not_supported'
        | 'map_view_error'
        | 'webmap_portal_load_error'
        | 'layerview_create_error'
        | 'fatal_error'
        | 'toggle_basemap_error'
        | 'toggle_basemap_load_error';
    errorMsg?: string;
    isEmbeddedMap?: boolean;
    extraData?: unknown;
}) => {
    const errorMessage = getEsriMapErrorTitle(errorMsg ?? category);
    logError(error ?? new Error(errorMessage), {
        type: `esri.${category}`,
        esriCategory: category,
        esriIsEmbeddedMap: !!isEmbeddedMap,
        extraData: JSON.stringify(extraData ?? {}),
    });
};

/**
 * Generates a letter key corresponding to the given index.
 * Keys start at A = 0, then proceed up until Z.
 * Numbers after 25 become AA, up to AZ, then on to BA, and so forth.
 * @param index The index
 * @returns The corresponding key
 */
export function getMapKey(index: number): string {
    const ordinateA = 'a'.charCodeAt(0);
    const ordinateZ = 'z'.charCodeAt(0);
    const len = ordinateZ - ordinateA + 1;

    let s = '';
    while (index >= 0) {
        s = String.fromCharCode((index % len) + ordinateA) + s;
        index = Math.floor(index / len) - 1;
    }
    return s;
}

/**
 * Converts any string into a unique 32-bit integer. SDBM Is a non-cryptographic
 * hash function that provides reasonable security against collisions. We use this
 * to create unique IDs based on a string input.
 * @param str The string to get the hash for.
 * @returns The numerical hash.
 */
export const sdbmHash = (str: string): number => {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + (hash << 6) + (hash << 16) - hash;
    }

    return hash >>> 0;
};

export const moveLayerOnTop = (webMap: WebMap | undefined, layerKey: string) => {
    if (!webMap) {
        return;
    }
    const { layers } = webMap;
    const currentLayer = webMap.findLayerById(layerKey);
    const polylinesLayer = webMap.findLayerById(FEATURE_LAYERS_PROPERTIES.POLYLINES.layerKey);
    layers.reorder(polylinesLayer, layers.length - 1);
    layers.reorder(currentLayer, layers.length - 1);
};

export const logFeatureLayerWarning = (cacheKey: string, message: string) => {
    const fullMessage = `${cacheKey} - ${message}`;

    logWarn(fullMessage, {
        esriDebug: 'esri-debug',
        layerCacheKey: cacheKey,
    });
};
